import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtMoney, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { PaymentDialog } from "../components/PaymentDialog";
import { PropertyMap } from "../components/PropertyMap";
import { ShortVideos } from "../components/ShortVideos";
import { MapPin, Bed, ArrowsOut, Bathtub, Phone, EnvelopeSimple, ChatCircleDots, ChartLineUp, Sparkle, CheckCircle, PlayCircle, ShieldCheck } from "@phosphor-icons/react";

const MODE_INFO = {
  sale: { t: "Vente", price: (p) => p.sale_price, suffix: "" },
  longterm: { t: "Location longue durée", price: (p) => p.monthly_rent, suffix: " /mois" },
  event: { t: "Location courte durée", price: (p) => p.event_price_per_day, suffix: " /jour" },
};

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [tab, setTab] = useState("");
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [chatOpen, setChatOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [booking, setBooking] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);

  useEffect(() => {
    api.get(`/properties/${id}`).then(({ data }) => {
      setP(data);
      setTab(data.modes?.[0] || "sale");
    }).catch(() => navigate("/catalog"));
  }, [id, navigate]);

  if (!p) return <div className="min-h-screen bg-stone-50"><Navbar /><div className="py-24 text-center text-stone-400">Chargement…</div></div>;

  const media = p.media?.length ? p.media : ["https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"];
  const isEvent = tab === "event";

  const sendMessage = async () => {
    if (!user) { navigate("/auth"); return; }
    if (!msg.trim()) return;
    try {
      await api.post("/messages", { property_id: p.id, receiver_id: p.owner_id, text: msg });
      toast.success("Message envoyé au propriétaire !");
      setMsg(""); setChatOpen(false);
      navigate("/messages");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const startBooking = async (bookingType) => {
    if (!user) { navigate("/auth"); return; }
    const type = bookingType || (isEvent ? "event" : "longterm");
    if (type === "event" && (!range.from || !range.to)) { toast.error("Sélectionnez des dates."); return; }
    setBooking(true);
    try {
      const { data: bk } = await api.post("/bookings", {
        property_id: p.id,
        booking_type: type,
        start_date: type === "event" ? range.from.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
        end_date: type === "event" ? range.to.toISOString().slice(0, 10) : "",
        guests: 1,
      });
      setPendingBooking(bk);
      setPayOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBooking(false);
    }
  };

  const ai = p.ai_analysis;
  const videoEmbed = (() => {
    if (!p.video_url) return null;
    const m = p.video_url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
    if (m) return { type: "yt", src: `https://www.youtube.com/embed/${m[1]}` };
    return { type: "file", src: p.video_url };
  })();

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {/* GALLERY */}
        <div className="grid lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-stone-200 aspect-[16/10]">
            <img data-testid="detail-main-img" src={media[active]} alt={p.title} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-2 gap-3">
            {media.slice(0, 4).map((m, i) => (
              <button key={i} onClick={() => setActive(i)} className={`rounded-xl overflow-hidden border-2 aspect-square ${active === i ? "border-orange-800" : "border-transparent"}`}>
                <img src={m} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          {/* LEFT */}
          <div className="lg:col-span-2 min-w-0">
            <div className="flex items-center gap-2 text-stone-500 text-sm">
              <MapPin size={15} weight="fill" /> {p.address ? `${p.address}, ` : ""}{p.city}{p.country ? `, ${p.country}` : ""}
              {p.verification_status === "certified" && (
                <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold" data-testid="detail-certified"><CheckCircle weight="fill" size={12} /> Certifié</span>
              )}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-1">{p.title}</h1>

            <div className="flex flex-wrap gap-5 mt-5 text-stone-700">
              <span className="flex items-center gap-2"><ArrowsOut size={20} className="text-orange-800" /> {p.surface} m²</span>
              <span className="flex items-center gap-2"><Bed size={20} className="text-orange-800" /> {p.rooms} pièces</span>
              <span className="flex items-center gap-2"><Bathtub size={20} className="text-orange-800" /> {p.bathrooms} sdb</span>
            </div>

            <h2 className="font-display text-xl font-bold text-stone-900 mt-8">Description</h2>
            <p className="text-stone-600 mt-2 leading-relaxed whitespace-pre-line">{p.description || "Aucune description."}</p>

            {videoEmbed && (
              <>
                <h2 className="font-display text-xl font-bold text-stone-900 mt-8 flex items-center gap-2"><PlayCircle weight="fill" className="text-orange-800" /> Visite vidéo</h2>
                <div className="mt-3 rounded-2xl overflow-hidden border border-stone-200 aspect-video bg-black" data-testid="detail-video">
                  {videoEmbed.type === "yt" ? (
                    <iframe src={videoEmbed.src} title="Visite vidéo" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  ) : (
                    <video src={videoEmbed.src} controls className="w-full h-full" />
                  )}
                </div>
              </>
            )}

            <ShortVideos videos={p.short_videos} />

            {p.amenities?.length > 0 && (
              <>
                <h2 className="font-display text-xl font-bold text-stone-900 mt-8">Équipements</h2>
                <div className="flex flex-wrap gap-2 mt-3">
                  {p.amenities.map((a) => (
                    <span key={a} className="flex items-center gap-1.5 bg-white border border-stone-200 rounded-full px-3 py-1.5 text-sm text-stone-700">
                      <CheckCircle size={15} weight="fill" className="text-orange-800" /> {a}
                    </span>
                  ))}
                </div>
              </>
            )}

            {isEvent && p.event_use_cases?.length > 0 && (
              <>
                <h2 className="font-display text-xl font-bold text-stone-900 mt-8">Idéal pour</h2>
                <div className="flex flex-wrap gap-2 mt-3">
                  {p.event_use_cases.map((u) => <span key={u} className="bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-3 py-1.5 text-sm font-medium">{u}</span>)}
                </div>
              </>
            )}

            {/* AI ANALYSIS */}
            {ai && (
              <div className="mt-8 bg-slate-900 rounded-2xl p-6 text-white" data-testid="detail-ai-analysis">
                <div className="flex items-center gap-2"><Sparkle weight="fill" className="text-orange-400" /><span className="overline text-xs text-orange-400">Analyse patrimoniale IA</span></div>
                <div className="grid sm:grid-cols-3 gap-4 mt-4">
                  {[["Vente", ai.yield_sale], ["Longue durée", ai.yield_longterm], ["Courte durée", ai.yield_event]].map(([l, v]) => (
                    <div key={l} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="text-xs text-stone-300">{l}</div>
                      <div className="font-display text-2xl font-bold text-orange-400">{v || 0}%<span className="text-xs text-stone-400 font-sans"> /an</span></div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-stone-300 mt-4"><b className="text-white">Zone :</b> {ai.socio_economic}</p>
                <p className="text-sm text-stone-300 mt-2"><b className="text-white">Foncier :</b> {ai.land_value}</p>
                <p className="text-sm text-stone-200 mt-3 bg-orange-800/30 border border-orange-700/40 rounded-lg p-3">{ai.recommendation}</p>
              </div>
            )}

            {p.latitude && p.longitude && (
              <>
                <h2 className="font-display text-xl font-bold text-stone-900 mt-8 flex items-center gap-2"><MapPin weight="fill" className="text-orange-800" /> Localisation</h2>
                <div className="mt-3"><PropertyMap properties={[p]} height={300} single center={[p.latitude, p.longitude]} /></div>
              </>
            )}
          </div>

          {/* RIGHT - booking / contact */}
          <div>
            <div className="bg-white border border-stone-200 rounded-2xl p-6 sticky top-20">
              {/* mode tabs */}
              {p.modes?.length > 1 && (
                <div className="flex gap-1 bg-stone-100 rounded-full p-1 mb-4">
                  {p.modes.map((m) => (
                    <button key={m} data-testid={`detail-tab-${m}`} onClick={() => setTab(m)}
                      className={`flex-1 text-xs font-semibold py-2 rounded-full transition-colors ${tab === m ? "bg-white text-orange-800 shadow-sm" : "text-stone-500"}`}>
                      {MODE_INFO[m].t}
                    </button>
                  ))}
                </div>
              )}
              <div className="text-sm text-stone-500">{MODE_INFO[tab]?.t}</div>
              <div className="font-display text-3xl font-bold text-orange-800" data-testid="detail-price">
                {fmtMoney(MODE_INFO[tab]?.price(p))}<span className="text-base text-stone-400 font-sans">{MODE_INFO[tab]?.suffix}</span>
              </div>

              {isEvent && (
                <div className="mt-4 border border-stone-200 rounded-xl p-2 flex justify-center">
                  <Calendar mode="range" selected={range} onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
                    disabled={[{ before: new Date() }, ...(p.booked_dates || []).map((d) => new Date(d + "T00:00:00"))]} />
                </div>
              )}

              {tab === "sale" ? (
                <div className="mt-4 space-y-2 text-sm">
                  <a href={`tel:${p.contact_phone}`} className="flex items-center gap-2 text-stone-700 bg-stone-50 rounded-lg p-3"><Phone weight="fill" className="text-orange-800" size={18} /> {p.contact_phone || "Non renseigné"}</a>
                  <div className="flex items-center gap-2 text-stone-700 bg-stone-50 rounded-lg p-3"><EnvelopeSimple weight="fill" className="text-orange-800" size={18} /> {p.contact_email}</div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-stone-600 flex items-center gap-1"><ShieldCheck weight="fill" className="text-orange-800" size={16} /> Caution (5%)</span>
                      <span className="font-display font-bold text-orange-800">{fmtMoney((p.sale_price || 0) * 0.05)}</span>
                    </div>
                    <p className="text-[11px] text-stone-500 mt-1">Versez une caution sécurisée pour réserver ce bien. Les fonds sont détenus par Lomar Properties (intermédiaire anti-fraude) avant reversement.</p>
                    <Button data-testid="detail-caution-btn" onClick={() => startBooking("caution")} disabled={booking}
                      className="w-full mt-2 rounded-full bg-orange-800 hover:bg-orange-900 text-white h-10">
                      {booking ? "…" : "Verser la caution"}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button data-testid="detail-book-btn" onClick={() => startBooking()} disabled={booking}
                  className="w-full mt-4 rounded-full bg-orange-800 hover:bg-orange-900 text-white h-12">
                  {booking ? "…" : isEvent ? "Réserver & payer" : "Louer & payer le 1er mois"}
                </Button>
              )}

              <Button data-testid="detail-contact-btn" variant="outline" onClick={() => user ? setChatOpen(true) : navigate("/auth")}
                className="w-full mt-2 rounded-full h-11 border-stone-300">
                <ChatCircleDots size={18} className="mr-2" /> Contacter le propriétaire
              </Button>
              <div className="text-xs text-stone-400 text-center mt-3">Publié par {p.owner_name || "Propriétaire"}</div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Contacter le propriétaire</DialogTitle>
            <DialogDescription>Envoyez un message directement au propriétaire du bien.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="chat-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Bonjour, je suis intéressé par ce bien…" rows={4} />
          <Button data-testid="chat-send-btn" onClick={sendMessage} className="rounded-full bg-orange-800 hover:bg-orange-900 text-white">Envoyer</Button>
        </DialogContent>
      </Dialog>

      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} booking={pendingBooking} />
    </div>
  );
}
